export type HelpItem = {
  label: string;
  detail: string;
};

export type HelpSection = {
  title: string;
  body?: string;
  items?: HelpItem[];
  steps?: string[];
  callout?: string;
};

export type HelpTopic = {
  id: string;
  category: "Start here" | "Daily work" | "Reporting" | "Account" | "Administration";
  title: string;
  summary: string;
  keywords: string[];
  sections: HelpSection[];
  related: string[];
};

export const helpTopics: HelpTopic[] = [
  {
    id: "getting-started",
    category: "Start here",
    title: "Getting around Referral Source Tracker",
    summary: "A tour of navigation, global controls, status indicators, and the fastest way to complete everyday work.",
    keywords: ["navigation", "sidebar", "menu", "install", "alerts", "mentions", "sign out", "mobile"],
    sections: [
      {
        title: "Primary navigation",
        items: [
          { label: "Referral sources", detail: "Find organizations and professional relationships, review contact history, open a source, and log outreach." },
          { label: "Contacts", detail: "Browse the address book, export contacts, and open a person's full profile with interactions, events, referrals, and notes." },
          { label: "Tasks", detail: "Work assigned tasks and cadence-driven follow-ups in an agenda or calendar." },
          { label: "Events", detail: "Record relationship events, locations, referral-source links, attendees, and related files." },
          { label: "Reports", detail: "Review source coverage, outreach activity, referral counts, task performance, and user KPIs." },
          { label: "Admin", detail: "Available to admins for people, roles, categories, exports, archives, audit history, and workspace settings." },
          { label: "Help Center", detail: "Search all guidance or open an article for the screen you are currently using." },
        ],
      },
      {
        title: "Top-bar controls",
        items: [
          { label: "Menu", detail: "On smaller screens, opens the primary navigation drawer." },
          { label: "Install", detail: "Installs the application on a supported device so it can open like an app." },
          { label: "Alerts", detail: "Turns weekday browser follow-up notifications on or off. Browser permission is also required." },
          { label: "Mentions (@)", detail: "Shows contact-note mentions. Selecting one opens the related referral source and contact." },
          { label: "Question mark", detail: "Opens help for the current screen." },
          { label: "Add source", detail: "Creates a new referral-source relationship when your role permits it." },
        ],
      },
      {
        title: "Your account",
        items: [
          { label: "Profile area", detail: "Shows your avatar, name, and workspace role at the bottom of the sidebar." },
          { label: "All changes saved", detail: "Indicates that completed actions have been sent to the application." },
          { label: "Sign out", detail: "Securely ends the current session on this device." },
          { label: "Impersonation banner", detail: "Admins using Continue as user can return to their administrator account from the banner." },
        ],
      },
    ],
    related: ["sources", "contacts", "tasks", "notifications-account", "roles-privacy"],
  },
  {
    id: "sources",
    category: "Daily work",
    title: "Referral sources list",
    summary: "Search, filter, sort, save views, assess relationship health, and open referral-source records.",
    keywords: ["sources", "grid", "table", "sort", "days since contact", "filter", "saved view", "health", "bulk", "data quality", "hospital"],
    sections: [
      {
        title: "Relationship overview",
        items: [
          { label: "On track", detail: "Sources whose recent contact timing is within the expected follow-up cadence." },
          { label: "Need attention", detail: "Sources approaching or passing their expected contact timing." },
          { label: "Stale", detail: "Sources substantially beyond their expected cadence and likely needing outreach." },
          { label: "Need data cleanup", detail: "Opens the data-quality queue for records missing a contact, communication method, category, owner, or relationship notes." },
        ],
      },
      {
        title: "Find and organize records",
        items: [
          { label: "Search", detail: "Matches source names, contacts, categories, pinned information, and relationship notes." },
          { label: "Category", detail: "Limits the list to a referral category such as hospital, provider, or senior living." },
          { label: "Hospital status", detail: "Shows only designated hospitals or non-hospitals, independent of the source category." },
          { label: "Assigned user", detail: "For roles with team visibility, shows all owners, your sources, or one selected person’s sources." },
          { label: "Sort", detail: "Orders by name, hospital designation, category, referral volume, contact timing, owner, or relationship health." },
          { label: "A–Z / Z–A", detail: "Reverses the current sort direction. For dates and day counts, it reverses oldest/newest ordering." },
          { label: "Table / Cards", detail: "Opens in the scanning-friendly table by default; choose Cards for the visual layout without changing the records in view." },
          { label: "Saved view", detail: "Restores a saved combination of search, filters, data-quality mode, and sorting on this device." },
        ],
      },
      {
        title: "Cards and table rows",
        items: [
          { label: "Relationship health", detail: "A quick status calculated from contact timing and the source’s follow-up cadence." },
          { label: "Last contacted", detail: "The most recent recorded outreach date." },
          { label: "Days since contact", detail: "The calendar-day age of the latest recorded contact; records with no contact are clearly marked." },
          { label: "Contact next", detail: "The next cadence date or scheduled task date that should bring the relationship back into the queue." },
          { label: "Row selection", detail: "Select manageable table rows to change category, hospital designation, or assigned person in bulk." },
          { label: "Open record", detail: "Select a card or table row to open the full source record." },
          { label: "Log contact", detail: "Records an outreach action directly from the source list when your role permits it." },
        ],
      },
    ],
    related: ["source-editor", "source-record", "log-contact", "tasks"],
  },
  {
    id: "contacts",
    category: "Daily work",
    title: "Contacts and contact profiles",
    summary: "Use the cross-source address book, export contact data, and review a person's complete relationship history.",
    keywords: ["contacts", "address book", "business card", "scan", "profile", "timeline", "interactions", "attendance", "events", "notes", "export", "excel"],
    sections: [
      {
        title: "Address book",
        items: [
          { label: "Search and filters", detail: "Find people across referral sources by name, organization, title, phone, or email, then narrow the list by category or contact status." },
          { label: "Open a profile", detail: "Select a person's name to open their full profile while keeping Contacts active in the main sidebar." },
          { label: "Add or edit", detail: "Create a contact within a referral source or update their identity, communication preferences, address, and status when your role permits it." },
          { label: "Scan business cards", detail: "Take one card photo or select up to 12 saved photos for a batch. Review, correct, save, or skip each card in order. Every saved card is stored in the contact's photo gallery." },
          { label: "Multiple phone numbers", detail: "Store labeled work, direct, mobile, home, fax, or custom numbers. Choose the primary calling number and mark numbers that can receive text messages." },
          { label: "Export Excel", detail: "Downloads the contacts currently represented by the address book as an Excel workbook." },
        ],
      },
      {
        title: "Contact profile",
        items: [
          { label: "Profile", detail: "Shows the person's organization, role, direct contact details, preferred communication method, availability, status, and verification date." },
          { label: "Photos", detail: "Uploads any number of JPEG, PNG, WebP, HEIC, or HEIF pictures up to 20 MB each. The original is retained for download while an optimized WebP display copy is used for avatars and galleries. Photos also appear on the employer's referral-source page." },
          { label: "Interactions", detail: "Combines interactions logged with this person, direct notes, referral receipts, event attendance, and unassigned organization outreach in one chronological timeline." },
          { label: "Events", detail: "Lists every event visible to you where this person was recorded as an attendee, including networking events that have no referral source." },
          { label: "Notes", detail: "Shows notes attached directly to this contact, including pinned status and edit history." },
        ],
        callout: "Organization-level outreach is labeled separately from records tied directly to the person, so the profile does not overstate who participated.",
      },
      {
        title: "Profile actions",
        items: [
          { label: "Log interaction", detail: "Records a call, email, visit, meeting, or other outreach directly with this person while also updating organization reporting and cadence." },
          { label: "Add note", detail: "Saves durable context directly to the contact. You can pin it, mention a teammate, and optionally create a follow-up." },
          { label: "Record event", detail: "Opens the event editor with this contact already selected as an attendee. Events may be tied to any networking group and do not require the contact's referral source." },
          { label: "Call or email", detail: "Uses the person's direct contact information when the contact is active and the method is available." },
          { label: "Outreach", detail: "Opens Growth Operations with this person selected so you can use a template, send an email or open a call or text, automatically log the action, and schedule the next follow-up." },
          { label: "Edit contact", detail: "Opens the contact editor without leaving the profile. Saving refreshes the profile with the latest information." },
          { label: "Back to address book", detail: "Returns to the full contacts directory and its search, filter, and export tools." },
        ],
      },
    ],
    related: ["sources", "source-record", "events-files", "log-contact"],
  },
  {
    id: "source-editor",
    category: "Daily work",
    title: "Add or edit a referral source",
    summary: "A field-by-field guide to organization details, ownership, contact priority, notes, and individual contacts.",
    keywords: ["add source", "edit source", "category", "hospital", "priority", "daily", "weekly", "monthly", "cadence", "owner", "archive"],
    sections: [
      {
        title: "Organization fields",
        items: [
          { label: "Organization name", detail: "The searchable display name of the referral source." },
          { label: "Referral category", detail: "The type of organization or professional relationship. Admins manage available values." },
          { label: "Hospital designation", detail: "Marks the organization for hospital-specific filters, sorting, and referral reports even when its category is more specific." },
          { label: "Assigned to", detail: "The primary staff owner. This person receives the source in their follow-up work and weekday digest." },
          { label: "Priority", detail: "Daily, weekly, monthly, or another custom frequency that controls expected contact timing." },
          { label: "Use workspace default", detail: "For Other priority, uses the admin-defined default number of days between contacts." },
          { label: "Contact every … days", detail: "For Other priority, sets a source-specific custom interval." },
          { label: "Main phone", detail: "The organization’s central phone number, separate from an individual contact’s phone." },
          { label: "Website", detail: "The organization’s website. A missing scheme is normalized when saved." },
          { label: "Organization address", detail: "The main facility or business address, separate from contact-specific addresses." },
        ],
      },
      {
        title: "Notes",
        items: [
          { label: "Pinned source note", detail: "Prominent operational context displayed near the top of the source record. Its edit history is retained." },
          { label: "Note color", detail: "Gold, purple, and teal organize importance; red should be reserved for critical operational guidance." },
          { label: "Source notes", detail: "Imported or historical relationship notes kept separately from the pinned note." },
        ],
        callout: "Do not place diagnoses, dates of birth, medical records, or other clinical details in source notes.",
      },
      {
        title: "Individual contacts",
        items: [
          { label: "Add contact", detail: "Adds another person at the source. Each contact has independent communication and address information." },
          { label: "Primary", detail: "Marks the main person to contact. Only one contact can be primary." },
          { label: "Name / title", detail: "Identifies the person and their organizational role." },
          { label: "Phone / email", detail: "Direct contact methods for that person." },
          { label: "Contact address", detail: "The person’s own mailing or work address when it differs from the organization." },
          { label: "Preferred method", detail: "Any method, phone, email, text, or in-person visit." },
          { label: "Best time to reach", detail: "Useful availability guidance, such as weekday mornings." },
          { label: "Contact status", detail: "Active, former, unresponsive, or do not contact. Do-not-contact records suppress direct actions." },
          { label: "Last verified", detail: "The date the person’s details were last confirmed." },
          { label: "Remove contact", detail: "Removes that contact from the edited source when changes are saved." },
        ],
      },
      {
        title: "Archive source",
        body: "Archiving removes a source from active work without erasing its history. Admins can restore it later from Archived sources.",
      },
    ],
    related: ["sources", "source-record", "admin-data", "roles-privacy"],
  },
  {
    id: "source-record",
    category: "Daily work",
    title: "Referral source record",
    summary: "Use the full referral-source details page for notes, contacts, collaboration, referrals, files, history, and organization details.",
    keywords: ["details page", "source record", "contacts", "notes", "sticky note", "mentions", "referrals received", "history", "address"],
    sections: [
      {
        title: "Page overview and timing",
        items: [
          { label: "Summary", detail: "Shows contact, interaction, referral, and file totals at the top of the source page." },
          { label: "Section navigation", detail: "Jumps directly to overview, contacts, files and photos, or activity." },
          { label: "Back to previous screen", detail: "Returns to the exact in-app screen used to open this source." },
          { label: "Follow-up", detail: "Schedules a task tied to this source." },
          { label: "Edit source", detail: "Changes organization, owner, priority, notes, and contact information." },
          { label: "Log contact", detail: "Records a call, visit, email, event, or other outreach action." },
          { label: "Last contacted / Contact next", detail: "Shows relationship recency and the next expected or scheduled touch." },
          { label: "Shared record", detail: "Indicates that you can collaborate on the record but are not its assigned owner." },
        ],
      },
      {
        title: "Notes and collaboration",
        items: [
          { label: "Pinned source note", detail: "At-a-glance standing information. Edit history records earlier text, color, date, and editor." },
          { label: "Imported / relationship notes", detail: "Historical notes brought from the source workbook and kept separate from the pinned note." },
          { label: "Contact note", detail: "Adds a dated note to one specific contact. Other staff may add notes to shared sources." },
          { label: "Mention a teammate", detail: "Creates an in-app notification and sends that person an email with context and an application link." },
          { label: "Create follow-up", detail: "Optionally creates a task while adding a contact note." },
          { label: "Pinned contact note", detail: "Keeps an important contact-specific note prominent. Edits retain history." },
          { label: "Referrals received", detail: "Records an aggregate quantity and date for a contact without patient information." },
        ],
      },
      {
        title: "Contacts and organization details",
        items: [
          { label: "Contact badges", detail: "Show preferred method, status, and best time to reach." },
          { label: "Contact address", detail: "Opens a map search for that individual contact’s address." },
          { label: "Phone / email", detail: "Starts a device call or email for active contacts." },
          { label: "Do not contact", detail: "Suppresses direct actions and displays a warning to use another contact." },
          { label: "Organization details", detail: "Shows the source’s main phone, facility address, and website independently from individual contacts." },
          { label: "Activity history", detail: "Lists recorded outreach with action type, author, notes, referrals, and date." },
        ],
      },
    ],
    related: ["log-contact", "events-files", "source-editor", "roles-privacy"],
  },
  {
    id: "log-contact",
    category: "Daily work",
    title: "Log outreach and referrals",
    summary: "Record what happened, classify the action, and count a referral while keeping clinical information out of the tracker.",
    keywords: ["log contact", "action", "activity type", "visit", "call", "email", "marketing", "referral name", "received by"],
    sections: [
      {
        title: "Contact log fields",
        items: [
          { label: "Action type", detail: "Classifies the interaction for history and reports, such as phone call, email, visit, meeting, marketing, or event." },
          { label: "Quick outcome", detail: "Fills the summary with a common outcome; it can still be edited." },
          { label: "What happened?", detail: "A concise required summary of the interaction." },
          { label: "Notes", detail: "Optional non-clinical context, outcomes, or next steps." },
          { label: "Save contact", detail: "Adds the activity to the source, updates last-contact timing, and resolves the related task when applicable." },
        ],
      },
      {
        title: "When a referral came from the source",
        items: [
          { label: "A referral came from this source", detail: "Adds a referral event to this contact log so source and user reports count it." },
          { label: "Received by", detail: "The staff member credited with receiving the referral." },
          { label: "Referral name", detail: "The referred person’s name only, used to distinguish the referral record." },
        ],
        callout: "Enter a name only. Keep diagnoses, dates of birth, insurance details, clinical notes, and medical documents in the approved clinical system.",
      },
    ],
    related: ["source-record", "reports", "roles-privacy", "tasks"],
  },
  {
    id: "tasks",
    category: "Daily work",
    title: "Tasks and follow-ups",
    summary: "Manage due work, cadence reminders, rescheduling, priorities, recurrence, ownership, and calendar planning.",
    keywords: ["tasks", "follow ups", "today", "overdue", "calendar", "agenda", "snooze", "reschedule", "recurrence", "priority", "assign"],
    sections: [
      {
        title: "Task page",
        items: [
          { label: "Overdue / Due today / Upcoming", detail: "Summary counts for the work currently in the queue." },
          { label: "Agenda", detail: "Groups tasks and cadence follow-ups by timing for focused daily work." },
          { label: "Calendar", detail: "Shows dated tasks by month. Authorized users can drag a task to another day while preserving its time." },
          { label: "New task", detail: "Creates general work or a task tied to a referral source." },
          { label: "Contacted", detail: "Opens the contact log for a source-related task." },
          { label: "Complete", detail: "Marks a non-contact task finished. Repeating tasks create their next occurrence." },
          { label: "Reschedule", detail: "Moves a task or cadence reminder to tomorrow, next week, 30 days out, or a selected date." },
          { label: "Open source", detail: "Opens the linked source record for context." },
        ],
      },
      {
        title: "Task editor fields",
        items: [
          { label: "My current tasks", detail: "The signed-in landing dashboard shows your open assignments immediately, including priority and due date. View all tasks opens the complete agenda." },
          { label: "Referral source", detail: "Optionally connects the task to a source; leaving it blank creates general workspace work." },
          { label: "Task title", detail: "The specific action to complete." },
          { label: "Details", detail: "Supporting context and expected outcome." },
          { label: "Due date and time", detail: "Controls placement in the agenda, calendar, and reminder delivery." },
          { label: "Priority", detail: "Low, normal, high, or urgent visual urgency." },
          { label: "Assigned to", detail: "The user responsible for the task. Managers and admins can assign work to teammates; a new assignee receives an in-app notification and task email." },
          { label: "Repeat", detail: "No repeat, daily, weekly, monthly, or a custom number of days." },
        ],
      },
      {
        title: "Cadence follow-ups versus tasks",
        body: "A source’s daily, weekly, monthly, or custom priority automatically determines when outreach is due. A scheduled task is explicit work with an owner and due time. Rescheduling a cadence reminder creates dated work without changing the source’s underlying priority.",
      },
    ],
    related: ["sources", "log-contact", "notifications-account", "roles-privacy"],
  },
  {
    id: "events-files",
    category: "Daily work",
    title: "Events, networking groups, photos, and documents",
    summary: "Record past or future events, maintain recurring networking-group profiles, and manage source or event attachments.",
    keywords: ["events", "networking groups", "cadence", "future dates", "attendees", "location", "photos", "documents", "attachments", "files", "upload", "download", "R2"],
    sections: [
      {
        title: "Events page",
        items: [
          { label: "Table / Cards", detail: "Opens with scanning-friendly rows by default; choose Cards for the visual event grid." },
          { label: "Events recorded", detail: "Total events visible to your role." },
          { label: "Future dates", detail: "Events scheduled after today, including upcoming networking-group meetings." },
          { label: "People represented", detail: "Unique staff and address-book contacts recorded as attendees." },
          { label: "Referral source link", detail: "Opens the linked source record." },
          { label: "Paperclip count", detail: "The number of direct event photos and source attachments assigned to that event." },
          { label: "Add contacts", detail: "From an event’s details page, select anyone in the address book who attended or create a missing contact and add them without leaving the event." },
          { label: "Edit", detail: "Managers and admins can change event details and attendees." },
        ],
      },
      {
        title: "Event editor fields",
        items: [
          { label: "Event name", detail: "A recognizable event title." },
          { label: "Primary referral source", detail: "Optional contextual link for where the event occurred or which relationship it primarily supported. This is separate from attendance." },
          { label: "Networking group", detail: "Optionally connects this date to a reusable group profile such as HPANWFL." },
          { label: "Event date and time", detail: "When the event happened or will happen. Future events may be saved before attendance is known." },
          { label: "Location", detail: "Facility, venue, or meeting location." },
          { label: "Event notes", detail: "Purpose, outcomes, conversations, or next steps without patient information." },
          { label: "Event photo", detail: "Uploads a JPEG, PNG, or WebP photo up to 10 MB directly to the event when it is saved. Existing photos can be opened or removed while editing." },
          { label: "Staff who attended", detail: "Select every user who was present. Select all and Clear speed up large changes." },
          { label: "Referral sources tab", detail: "Select every organization represented at the event, regardless of the event’s primary source. Search by organization name or category." },
          { label: "Contacts tab", detail: "Choose any individual contact who attended. If the person is missing, create them from the search; they are added to the address book and selected without losing the event draft." },
        ],
      },
      {
        title: "Networking groups",
        items: [
          { label: "Networking groups tab", detail: "Lists recurring relationship communities separately from one-time events." },
          { label: "Group details", detail: "Stores the group’s purpose, website, primary location, meeting cadence, meeting pattern, and shared notes." },
          { label: "Future dates", detail: "Schedule upcoming meetings as event records so attendance and outcomes can be completed later." },
          { label: "Meeting history", detail: "Shows every linked past meeting with its location, notes, staff, and contact attendance." },
          { label: "Contacts met", detail: "Builds a group-specific relationship directory from address-book contacts who attended completed meetings." },
          { label: "Direct profile link", detail: "A group profile can be bookmarked and reopened directly from its URL." },
        ],
      },
      {
        title: "Photos directly on an event",
        items: [
          { label: "Choose a photo", detail: "Selects a JPEG, PNG, or WebP image up to 10 MB and shows a preview before saving." },
          { label: "Save event", detail: "Saves the event first, then privately uploads the selected photo to the event record." },
          { label: "Event gallery", detail: "Editing an event shows its existing photos with file size, uploader, open, and remove controls." },
          { label: "Event card photo", detail: "The newest attached photo appears on the event card and in networking-group meeting history." },
        ],
      },
      {
        title: "Photos and documents on a source",
        items: [
          { label: "Choose file / Upload", detail: "Adds a common image, PDF, text, CSV, RTF, Word, Excel, or PowerPoint file up to 25 MB." },
          { label: "Assign to event", detail: "Optionally associates the file with an event already linked to the same referral source." },
          { label: "Caption", detail: "Explains what the file is and why it matters." },
          { label: "Open", detail: "Previews supported images and PDFs through an authenticated application route." },
          { label: "Download", detail: "Downloads the original file." },
          { label: "Edit details", detail: "Changes the event assignment or caption without re-uploading." },
          { label: "Delete permanently", detail: "Removes the attachment metadata and stored file after confirmation." },
        ],
        callout: "Attachments are for relationship materials, event photos, flyers, and operational documents—not patient charts or clinical records.",
      },
    ],
    related: ["source-record", "roles-privacy", "reports"],
  },
  {
    id: "growth-operations",
    category: "Reporting",
    title: "Growth Operations",
    summary: "Automate outreach, connect calendars, prioritize relationships, add tenant-specific data, analyze performance, and connect outside systems.",
    keywords: ["growth", "automation", "template", "calendar", "Google", "Microsoft", "relationship intelligence", "health score", "custom fields", "tags", "ROI", "API", "webhook", "scheduled report"],
    sections: [
      {
        title: "Outreach and follow-up automation",
        items: [
          { label: "Quick outreach", detail: "Choose an active contact, select email, text, or call, optionally apply a shared template, and schedule the next follow-up. Email is queued securely; calls and texts open on the current device. Successful actions are recorded in relationship history." },
          { label: "Templates", detail: "Administrators create reusable email, text, and call scripts with merge fields for first name, full name, organization, and sender." },
          { label: "Automation rules", detail: "Administrators create task-generating rules for stale relationships, overdue follow-ups, or new contacts. Rules avoid duplicating an existing open automation task." },
        ],
      },
      {
        title: "Calendar and relationship intelligence",
        items: [
          { label: "Google / Microsoft", detail: "Each user can connect a personal Google or Microsoft calendar after provider OAuth credentials are configured. Hourly synchronization imports provider events and publishes workspace events where the user is an attendee." },
          { label: "Health score", detail: "A prioritization signal based on contact recency, overdue work, active contacts, recent activity, and referrals. It is intended to surface drift, not grade a person." },
          { label: "Recommended next step", detail: "Explains the strongest current signal—such as a missing active contact, overdue work, or a quiet relationship—and provides a direct path back to the source or outreach." },
        ],
      },
      {
        title: "Custom data and insights",
        items: [
          { label: "Custom fields", detail: "Administrators define text, number, date, select, yes/no, or URL fields for sources or contacts. Authorized users enter values from the record organizer." },
          { label: "Tags", detail: "Administrators create shared color-coded tags; authorized users assign them to sources and contacts." },
          { label: "Insights", detail: "Compare outreach, referrals, conversions, completed follow-ups, relationship trends, source yield, conversion rate, and team contribution across selectable periods." },
          { label: "Scheduled reports", detail: "Administrators send weekly or monthly snapshots to active workspace users. Reports are generated from current data at delivery time." },
        ],
      },
      {
        title: "Integrations",
        items: [
          { label: "API tokens", detail: "Administrators issue separate, expiring Bearer tokens with sources:read, contacts:read, or activities:write scopes. The token is shown once and stored only as a hash." },
          { label: "Signed webhooks", detail: "Administrators register HTTPS endpoints and subscribe to record events. Deliveries include a unique ID, event type, and HMAC SHA-256 signature and retry transient failures." },
          { label: "Read-only reporting", detail: "Reports-role users see only Relationship Intelligence and Insights. Mutation workflows remain hidden and server-side permissions still apply." },
        ],
      },
    ],
    related: ["contacts", "reports", "tasks", "roles-privacy"],
  },
  {
    id: "reports",
    category: "Reporting",
    title: "Reports and performance KPIs",
    summary: "Use source, hospital, user, and source-by-user reports; filter one person or period; and export CSV or PDF.",
    keywords: ["reports", "KPI", "performance", "export", "PDF", "CSV", "single user", "visits", "calls", "referrals", "source by user", "hospital"],
    sections: [
      {
        title: "Report navigation",
        items: [
          { label: "Source overview", detail: "Organization-level activity, referral totals, last interaction, contacts, and task status." },
          { label: "Hospital referrals", detail: "Ranks designated hospitals by referral count for the selected period and shows hospital outreach and follow-up gaps." },
          { label: "User performance", detail: "Outreach, relationship coverage, referral credit, task completion, timeliness, and activity mix by user." },
          { label: "Source by user", detail: "Counts how often each user called, emailed, visited, met, marketed to, or otherwise contacted each source." },
          { label: "Referral attribution", detail: "Shows which source, source contact, and staff member received credit for referral activity." },
          { label: "Goals", detail: "Sets monthly, quarterly, or annual targets for outreach, referrals, move-ins, or completed follow-ups and compares them with live workspace activity." },
        ],
      },
      {
        title: "User performance controls and KPIs",
        items: [
          { label: "Person", detail: "Runs the report for all users or one selected user." },
          { label: "Period", detail: "Limits activity to the selected reporting window while retaining current task status where noted." },
          { label: "Table / Cards", detail: "Opens in the table by default; choose Cards to change presentation without changing the calculation." },
          { label: "Export CSV", detail: "Downloads report rows for spreadsheet analysis." },
          { label: "Export PDF", detail: "Creates a formatted report suitable for review or sharing." },
          { label: "Contacts logged", detail: "Recorded source outreach during the period." },
          { label: "Referrals received", detail: "Referral events credited to the user." },
          { label: "Relationship coverage", detail: "The portion of owned sources reached during the period." },
          { label: "Task completion", detail: "Completed tasks compared with tasks due." },
          { label: "Completed on time", detail: "Tasks completed by their due date and time." },
          { label: "Activity mix", detail: "Calls, emails, visits, and other outreach recorded by the user." },
        ],
      },
      {
        title: "Source-by-user details",
        items: [
          { label: "Total", detail: "All contact activities recorded by the user for that source." },
          { label: "Referrals", detail: "Referral quantity credited through that source/user combination." },
          { label: "Calls/texts, emails, visits, meetings, events, marketing, other", detail: "Breakdown based on the Action type selected when contact was logged." },
          { label: "Last activity", detail: "Most recent contact or referral date represented in the row." },
          { label: "Open row/card", detail: "Opens the referral source for underlying context." },
        ],
      },
    ],
    related: ["log-contact", "sources", "tasks", "roles-privacy"],
  },
  {
    id: "notifications-account",
    category: "Account",
    title: "Notifications, installation, and account access",
    summary: "Manage task notifications, browser alerts, mentions, weekday email digests, installation, sign-in, passwords, and activation links.",
    keywords: ["notifications", "tasks", "assigned", "alerts", "mentions", "email", "digest", "install", "PWA", "password", "reset", "activation", "login", "sign out"],
    sections: [
      {
        title: "Notifications",
        items: [
          { label: "Alerts", detail: "Requests browser permission and enables weekday follow-up notifications for the current account and device." },
          { label: "Blocked alerts", detail: "If permission was denied, change the site permission in the browser before trying again." },
          { label: "Notifications", detail: "The bell menu lists new task assignments, reassignments, and contact-note mentions. Opening a task notification takes you to your task agenda." },
          { label: "Task assignment email", detail: "When another user assigns or reassigns a task to you, an email includes the task, assigner, due date, priority, source context, and an Open task button." },
          { label: "Mentions", detail: "Contact-note mentions appear in Notifications. Mentioned users also receive an email explaining the context with a button back to the application." },
          { label: "Weekday digest", detail: "An account-level email summary of due referral sources. Admins control workspace delivery time and each user’s opt-in." },
        ],
      },
      {
        title: "Install and sign in",
        items: [
          { label: "Install", detail: "On supported browsers, places the tracker on the home screen or app launcher." },
          { label: "Sign in", detail: "Uses the email address and password associated with the workspace account." },
          { label: "Forgot your password?", detail: "Emails a time-limited secure link. Request another link if the first one expires." },
          { label: "Set password", detail: "Passwords must contain at least eight characters and match the confirmation." },
          { label: "Activation email", detail: "New users choose a password from an emailed link. Admins can resend an expired activation link." },
          { label: "Sign out", detail: "Ends the session; use it on shared or unattended devices." },
        ],
      },
    ],
    related: ["getting-started", "admin-people", "tasks", "roles-privacy"],
  },
  {
    id: "admin-overview",
    category: "Administration",
    title: "Administration overview",
    summary: "Understand every administration area and when to use it.",
    keywords: ["admin", "settings", "people", "categories", "imports", "exports", "archives", "audit", "workspace", "branding", "logo", "colors"],
    sections: [
      {
        title: "Admin tools",
        items: [
          { label: "People & access", detail: "Invite users, assign roles, manage avatars and digests, reset passwords, resend activation, impersonate, suspend access, or delete a user and transfer their active work." },
          { label: "Source categories", detail: "Manage referral categories and outreach Action labels used in forms and reports." },
          { label: "Imports & exports", detail: "Upload mapped CSV or Excel data with a preview and duplicate protection, safely roll back unused imports, and download source or referral data." },
          { label: "Data quality", detail: "Work through high-priority missing details, stale verification, relationship gaps, and possible duplicate groups from one cleanup inbox." },
          { label: "Archived sources", detail: "Find and restore source records removed from active work." },
          { label: "Audit history", detail: "Review who changed workspace records and when." },
          { label: "Workspace settings", detail: "Set tenant branding, logo, colors, workspace identity, timezone, digest time, default contact cadence, and digest delivery." },
        ],
      },
    ],
    related: ["admin-people", "admin-data", "roles-privacy"],
  },
  {
    id: "admin-people",
    category: "Administration",
    title: "People, avatars, roles, and access",
    summary: "Invite and activate users, manage photos and roles, control digests, reset passwords, impersonate safely, suspend access, and delete users with work reassignment.",
    keywords: ["people", "invite", "user", "avatar", "photo", "role", "digest", "password", "impersonate", "activation", "suspend", "delete", "reassign"],
    sections: [
      {
        title: "Invite a teammate",
        items: [
          { label: "Name / email", detail: "Creates the user identity and determines where the activation email is sent." },
          { label: "Role", detail: "Controls visible navigation, source access, editing, reports, task assignment, and administration." },
          { label: "Weekday digest", detail: "Opts the new user into weekday due-source emails." },
          { label: "Send invitation", detail: "Creates the account and emails a secure password-setup link." },
        ],
      },
      {
        title: "Workspace person controls",
        items: [
          { label: "Camera button", detail: "Uploads or replaces a JPEG, PNG, or WebP profile photo up to 5 MB." },
          { label: "Remove photo", detail: "Returns the user to an initials avatar after confirmation." },
          { label: "Role", detail: "Changes access immediately. Use the least privilege needed for the person’s work." },
          { label: "Digest", detail: "Turns that person’s weekday follow-up email on or off." },
          { label: "Key button", detail: "Sets another active user’s password and signs out their other sessions." },
          { label: "Continue as user", detail: "Lets an admin verify the application using that user’s permissions. The banner returns to the admin session." },
          { label: "Resend activation", detail: "Sends a fresh activation email for an account whose original link is pending or expired." },
          { label: "Active / Reactivate", detail: "Deactivation revokes sessions and workspace access without deleting historical attribution. Reactivate restores access." },
        ],
      },
    ],
    related: ["roles-privacy", "notifications-account", "admin-overview"],
  },
  {
    id: "admin-data",
    category: "Administration",
    title: "Categories, imports, archives, audit, and workspace settings",
    summary: "Configure dropdown values, move data safely, restore sources, review changes, and control workspace defaults.",
    keywords: ["category", "action type", "lookup", "import", "export", "CSV", "archive", "restore", "audit", "timezone", "default cadence"],
    sections: [
      {
        title: "Source categories and activity labels",
        items: [
          { label: "Source category", detail: "Values available in source records and source filtering." },
          { label: "Activity type", detail: "Action values available when logging contact and used to group reporting counts." },
          { label: "Add option", detail: "Creates a new label without changing historical records." },
          { label: "Active", detail: "Controls whether an option is offered for new entries; inactive values can remain on history." },
        ],
      },
      {
        title: "Imports, exports, and archives",
        items: [
          { label: "Upload and map", detail: "Accepts CSV or Excel files up to 1,000 rows. Map the organization field and any available organization or contact columns." },
          { label: "Preview", detail: "Shows which rows will create sources, add contacts, or skip obvious duplicates. Imports stay disabled until flagged rows are resolved." },
          { label: "Import history", detail: "Shows workbook filename, environment, created source/contact counts, skipped duplicates, status, time, and unresolved issues." },
          { label: "Roll back", detail: "Archives the records created by an import only when none of those records has since been edited or used. This protects subsequent work." },
          { label: "Export sources", detail: "Downloads current source records as CSV." },
          { label: "Export referrals", detail: "Downloads referral records as CSV for authorized review." },
          { label: "Archived sources", detail: "Lists inactive source records and their archive dates." },
          { label: "Restore", detail: "Returns an archived source to active lists with its history intact." },
          { label: "Data-quality inbox", detail: "Prioritizes missing communication methods, contacts that need verification, sources without people, and relationships without recent outreach. A check can be dismissed for 30 days." },
        ],
      },
      {
        title: "Audit and workspace settings",
        items: [
          { label: "Audit history", detail: "Shows actor, action, record type, timestamp, and recorded change details. Search or filter by person, action, record type, and date; sort the results and move through them page by page." },
          { label: "Workspace name", detail: "The organization identity shown in workspace data." },
          { label: "Brand display name", detail: "The tenant name shown to signed-in users in the sidebar and application header." },
          { label: "Organization logo", detail: "Uploads, replaces, or removes the private tenant logo. JPEG, PNG, and WebP files up to 5 MB are supported." },
          { label: "Primary color", detail: "Applies the tenant color to navigation, primary buttons, key source actions, and status surfaces." },
          { label: "Accent color", detail: "Applies a secondary tenant color to active navigation details, status indicators, and visual highlights." },
          { label: "Live workspace preview", detail: "Shows the display name, logo or monogram, and selected colors before settings are saved." },
          { label: "Default days between contacts", detail: "Updates every source configured to use the workspace default." },
          { label: "Timezone", detail: "Controls date grouping, due calculations, and digest scheduling." },
          { label: "Digest delivery time", detail: "The local hour for weekday follow-up emails." },
          { label: "Send weekday follow-up digests", detail: "Master switch for workspace digest delivery." },
        ],
      },
    ],
    related: ["admin-overview", "source-editor", "reports", "roles-privacy"],
  },
  {
    id: "roles-privacy",
    category: "Account",
    title: "Roles, permissions, and privacy",
    summary: "Know what each role can do and what information belongs—or does not belong—in Referral Source Tracker.",
    keywords: ["roles", "admin", "manager", "staff", "reports", "task user", "read only", "privacy", "PHI", "patient", "security"],
    sections: [
      {
        title: "Workspace roles",
        items: [
          { label: "Admin", detail: "Full workspace access, source ownership changes, user and role administration, settings, exports, archives, audit, reports, tasks, and events." },
          { label: "Manager", detail: "Team operational work including task assignment, event management, source collaboration, and team reports, without account administration." },
          { label: "Staff", detail: "Referral-source outreach and collaboration based on assigned relationships; can add notes to shared contacts and log permitted activity." },
          { label: "Reports", detail: "Read-only access to organization sources, events, and reports. Cannot change records." },
          { label: "Assigned tasks only", detail: "Works tasks assigned to them and may independently log outreach on a source they own. Referral records and broad team data are restricted." },
        ],
      },
      {
        title: "Information appropriate for this system",
        items: [
          { label: "Store", detail: "Organization and professional contact details, relationship notes, outreach outcomes, task instructions, event details, aggregate referral counts, and a referral name when logging receipt." },
          { label: "Do not store", detail: "Diagnoses, medical histories, dates of birth, insurance identifiers, treatment information, patient charts, or clinical documents." },
          { label: "Attachments", detail: "Use for event photos, relationship materials, flyers, and operational documents—not medical records." },
          { label: "Referral logs", detail: "Use the name field only to distinguish a referral; keep all clinical detail in the approved clinical system." },
        ],
        callout: "Use the minimum information necessary. If content belongs in a patient chart, it does not belong in Referral Source Tracker.",
      },
      {
        title: "Security habits",
        items: [
          { label: "Sign out", detail: "Always sign out on shared devices." },
          { label: "Passwords", detail: "Use a unique password of at least eight characters and do not share accounts." },
          { label: "Role changes", detail: "Admins should grant only the access needed and suspend or delete accounts promptly when access is no longer required." },
          { label: "Private files", detail: "Files and avatars are served through authenticated application routes rather than public links." },
        ],
      },
    ],
    related: ["admin-people", "source-record", "log-contact", "events-files"],
  },
];

export const helpTopicById = new Map(helpTopics.map((topic) => [topic.id, topic]));

export function helpTopicSearchText(topic: HelpTopic): string {
  return [
    topic.title,
    topic.summary,
    ...topic.keywords,
    ...topic.sections.flatMap((section) => [
      section.title,
      section.body ?? "",
      section.callout ?? "",
      ...(section.steps ?? []),
      ...(section.items ?? []).flatMap((item) => [item.label, item.detail]),
    ]),
  ].join(" ").toLowerCase();
}
